import React, { Fragment, FunctionComponent } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import styles from './ConfirmDialog.module.scss';

export interface Props {
    open: boolean;
    title?: JSX.Element | string;
    question?: JSX.Element | string;
    subquestion?: JSX.Element | string;
    actions?: JSX.Element;
    elementAlignment?: string;
    isSuccess?: boolean;
    isForm?: boolean;
    className?: string;
    cancelButtonLabel?: string;
    confirmButtonLabel?: string;
    processing?: boolean;
    onCancel?: () => void;
    onConfirm?: () => void;
    id?: string;
}

export const ConfirmDialog: FunctionComponent<Props> = props => {
    if (!props.open) {
        return null;
    }

    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            props.onCancel?.();
        }
    };

    const dialogContent = (
        <div
            className={styles.overlay}
            onClick={handleOverlayClick}
        >
            <div className={clsx(styles.dialog, props.className)}>
                <div className={styles.dialogTitle}>
                    <FontAwesomeIcon
                        icon={faTimes}
                        className={styles.iconClose}
                        onClick={props.onCancel}
                    />
                </div>

                <div
                    className={clsx(
                        styles.dialogContent,
                        props.elementAlignment ? styles.leftAligned : styles.centered
                    )}
                >
                    {props.title && (
                        <div
                            className={clsx(styles.title, {
                                [styles.hasSpace]: props.question || props.subquestion
                            })}
                        >
                            {typeof props.title === 'string' ? <p>{props.title}</p> : props.title}
                        </div>
                    )}

                    {props.question && (
                        <p
                            className={clsx(
                                styles.question,
                                props.isForm && styles.isForm,
                                props.subquestion && styles.hasSpace
                            )}
                        >
                            {typeof props.question === 'string' ? props.question : props.question}
                        </p>
                    )}

                    {props.subquestion && (
                        <p className={styles.subquestion}>
                            {typeof props.subquestion === 'string' ? props.subquestion : props.subquestion}
                        </p>
                    )}
                </div>

                <div className={clsx(styles.dialogActions, props.isForm && styles.isForm)}>
                    {props.actions ? (
                        props.actions
                    ) : (
                        <Fragment>
                            {props.cancelButtonLabel && (
                                <Button
                                    id={
                                        props.id &&
                                        props.id.concat(props.cancelButtonLabel?.toLowerCase() || '').concat('_button')
                                    }
                                    label={props.cancelButtonLabel}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        event.stopPropagation();
                                        props.onCancel?.();
                                    }}
                                    variant="black"
                                    className={styles.button}
                                    disabled={props.processing}
                                />
                            )}
                            {props.confirmButtonLabel && (
                                <Button
                                    id={
                                        props.id &&
                                        props.id.concat(props.confirmButtonLabel?.toLowerCase() || '').concat('_button')
                                    }
                                    label={props.confirmButtonLabel}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        event.stopPropagation();
                                        props.onConfirm?.();
                                    }}
                                    variant={props.isSuccess ? 'green' : 'danger'}
                                    className={styles.button}
                                    isLoading={props.processing}
                                />
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(dialogContent, document.body);
};

export default ConfirmDialog;
